// libs
import clsx from 'clsx'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { prop } from 'ramda'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'

// Components
import { makeStyles } from '@material-ui/styles'
import {
  getCinderQuotaUsage,
  getComputeQuotaUsage,
  getNetworkQuotaUsage,
  getSimpleTenantUsage,
} from 'account/components/userManagement/tenants/new-actions'
import config from 'app-config'
import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'
import { RootState, useAppSelector } from 'app/store'
import DocumentMeta from 'core/components/DocumentMeta'
import ExternalLink from 'core/components/ExternalLink'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import ProgressGraph from 'core/components/graphs/ProgressGraph'
import Ellipsis from 'core/components/progress/loader-svgs/Ellipsis'
import Card from 'core/elements/card'
import Divider from 'core/elements/Divider'
import { HeaderPrimaryActionPortal, HeaderRefreshPortal } from 'core/elements/header/portals'
import Text from 'core/elements/Text'
import ToggleSwitch from 'core/elements/ToggleSwitch'
import useListAction from 'core/hooks/useListAction'
import useSelectorWithParams from 'core/hooks/useSelectorWithParams'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import Theme from 'core/themes/model'
import { isDevelopment, isLocalhost } from 'core/utils/helpers'
import { routeManager } from 'core/utils/routes'
import { listHypervisors, listResmgrHosts } from 'openstack/components/infrastructure/actions'
import HostsListPageHeader from 'openstack/components/infrastructure/hosts/HostsListPageHeader'
import {
  hypervisorsSelector,
  resmgrHostsSelector,
} from 'openstack/components/infrastructure/selectors'
import {
  listAllStatewiseVirtualMachinesLite,
  listAllVirtualMachinesLite,
  listStatewiseVirtualMachinesLite,
} from 'openstack/components/vms/actions'
import {
  allStatewiseVirtualMachinesLiteSelector,
  allVirtualMachinesLiteSelector,
} from 'openstack/components/vms/selectors'
import UsageMetrics from './UsageMetrics'

//const vmCountsSelector = makeVmCountsSelector()

const DashboardPage = () => {
  const classes = useStyles()
  const { history } = useReactRouter()
  const { prefs, updatePrefs } = useScopedPreferences()
  const { allTenants } = prefs
  const { currentTenant } = prefs
  const [quotas, setQuotas] = useState<any>({})
  const session = useSelector<RootState, SessionState>(prop(sessionStoreKey))
  const {
    userDetails: { displayName, role, id: userId },
    features,
  } = session
  const showAdminDashboard = isAdmin()

  useListAction(listHypervisors)
  useListAction(listResmgrHosts)
  // useListAction(listVirtualMachines)
  // useListAction(listAllVirtualMachines)

  const hosts = useSelector(resmgrHostsSelector)
  const hypervisors = useAppSelector(hypervisorsSelector)

  const { reload: statewiseVmsReload, loading: statewiseVmsLoading } = useListAction(
    listStatewiseVirtualMachinesLite,
    {
      loadOnDemand: true,
    },
  )
  const { reload: vmReload } = useListAction(listAllVirtualMachinesLite, {
    loadOnDemand: true,
  })
  const { reload: allReload, loading: allStatewiseVmsLoading } = useListAction(
    listAllStatewiseVirtualMachinesLite,
    {
      loadOnDemand: true,
    },
  )

  //VMS
  // const vmsLite = useAppSelector(virtualMachinesLiteSelector)
  // const vmsState = useAppSelector(statewiseVirtualMachinesLiteSelector)

  //ALL VMS
  const allVmsLite = useAppSelector(allVirtualMachinesLiteSelector)
  const allVmsState = useAppSelector(allStatewiseVirtualMachinesLiteSelector)
  const initialAllTenantsLoad = useRef(true)

  // Load VMs for all tenants
  useEffect(() => {
    // If allTenants is false, do not make any calls
    if (!allTenants || !initialAllTenantsLoad.current) {
      return
    }

    // If allVmsLite is not null, make the call
    if (allVmsLite?.length === 0) {
      vmReload(true)
    }

    // If allVmsState is not null, make the call
    if (allVmsState?.success === 0 && allVmsState?.warning === 0 && allVmsState?.error === 0) {
      allReload(true)
    }

    // Set initialLoad to false after the first load
    initialAllTenantsLoad.current = false
  }, [allTenants, allVmsState, allVmsLite, vmReload, allReload])

  // Load VMs for current tenant
  // useEffect(() => {
  //   // If allTenants is true, do not make any calls
  //   if (allTenants || !initialVMsLoad.current) {
  //     return
  //   }

  //   // If vmsLite is not null, make the call
  //   if (vmsLite?.length === 0) {
  //     vmReload(true)
  //   }

  //   // If vms is not null, make the call
  //   if (vmsState?.success === 0 && vmsState?.warning === 0 && vmsState?.error === 0) {
  //     statewiseVmsReload(true)
  //   }

  //   // Set initialLoad to false after the first load
  //   initialVMsLoad.current = false
  // }, [allTenants, vmsState, vmsLite, vmReload, allReload])

  // const vmCounts = useMemo(() => {
  //   return allTenants ? allVmsState : vmsState
  // }, [vmsState, allVmsState, allTenants])

  // const { computeQuotas } = useSelectorWithParams(vmCountsSelector, {
  //   allTenants: allTenants as boolean,
  // })

  // const computeQuotaUsage = useMemo(() => {
  //   return computeQuotas?.[currentTenant as string]
  // }, [computeQuotas, currentTenant])

  const hypervisorCounts = useMemo(() => {
    const resmgrHostIds = hosts.map((host) => host.id)
    const validHypervisors = hypervisors.filter((hypervisor) =>
      resmgrHostIds.includes(hypervisor?.service?.host),
    )
    const counts = validHypervisors.reduce(
      (accum, hypervisor) => {
        if (hypervisor.status === 'enabled') {
          return {
            ...accum,
            success: accum.success + 1,
          }
        } else {
          return {
            ...accum,
            error: accum.error + 1,
          }
        }
      },
      {
        success: 0,
        error: 0,
      },
    )
    const hypervisorResmgrIds = validHypervisors.map((hypervisor) => hypervisor?.service?.host)
    const hypervisorHosts = hosts.filter((host) => host.displayRoles?.includes('Hypervisor'))
    const missingScheduling = hypervisorHosts.filter(
      (host) => !hypervisorResmgrIds.includes(host.id),
    )
    return {
      ...counts,
      error: counts.error + missingScheduling.length,
    }
  }, [hypervisors, hosts])

  useEffect(() => {
    if (!userId || !currentTenant) {
      return
    }
    const makeCalls = async () => {
      let computeQuotas = {}
      let cinderQuotas = {}
      let networkQuotas = {}
      let simpleTenantUsage = {}
      try {
        computeQuotas = await getComputeQuotaUsage({ tenantId: currentTenant })
      } catch (err) {
        console.error(err)
      }
      try {
        cinderQuotas = await getCinderQuotaUsage({ tenantId: currentTenant })
      } catch (err) {
        console.error(err)
      }
      try {
        networkQuotas = await getNetworkQuotaUsage({ tenantId: currentTenant })
      } catch (err) {
        console.error(err)
      }
      try {
        simpleTenantUsage = await getSimpleTenantUsage({ tenantId: currentTenant })
      } catch (err) {
        console.error(err)
      }
      setQuotas({
        ...computeQuotas,
        ...cinderQuotas,
        ...networkQuotas,
        simpleTenantUsage,
      })
    }
    makeCalls()
  }, [userId, currentTenant])

  const tenantUsage = useMemo(() => {
    return (
      (quotas?.simpleTenantUsage?.server_usages || [])?.reduce(
        (accum, usage) => {
          return {
            ...accum,
            vcpus: (accum.vcpus || 0) + usage.vcpus,
            ram: (accum.ram || 0) + usage.memory_mb,
          }
        },
        {
          vcpus: 0,
          ram: 0,
        },
      ) || { vcpus: 0, ram: 0 }
    )
  }, [quotas.simpleTenantUsage])

  // Create Grafana URL
  const grafanaURL = useMemo(() => {
    if (isDevelopment() || isLocalhost()) {
      return `${config.apiHost}/grafana/login`
    }
    return `${window.location.origin}/grafana/login`
  }, [])

  return (
    <>
      <DocumentMeta title="Dashboard" />
      {/* Show Grafana link only when allTenants is true */}
      <HeaderRefreshPortal>
        <div className={classes.headerRefreshPortal}>
          {allTenants ? (
            <Text variant="subtitle2">Resource Utilization</Text>
          ) : (
            <Text variant="subtitle2">Tenant Quota Usage</Text>
          )}
        </div>
      </HeaderRefreshPortal>
      <HeaderPrimaryActionPortal>
        {/* Just remove this allTenants condition if we decide to show grafana link for tenant only */}
        {allTenants && (
          <>
            <ExternalLink textVariant="caption1" url={grafanaURL} className={classes.externalLink}>
              View Grafana Dashboard
            </ExternalLink>
            <div className={classes.dividerTop} />
          </>
        )}
        {showAdminDashboard ? (
          <>
            <ToggleSwitch
              active={allTenants as boolean}
              onClick={() => updatePrefs({ allTenants: !allTenants })}
              label="Show All Tenants Info"
            />
          </>
        ) : null}
      </HeaderPrimaryActionPortal>
      {allTenants && (
        <div className={classes.resourceStatuses}>
          <Card className={classes.statusCard}>
            <div className={classes.cardBody}>
              <div className={classes.statusHeader}>
                <Text variant="subtitle2">Virtual Machines</Text>
                <Text
                  variant="caption1"
                  className={classes.seeAll}
                  onClick={() => history.push(routeManager.routes.openstack.vms.path())}
                >
                  See All{' '}
                  <FontAwesomeIcon regular className={classes.arrow}>
                    chevron-right
                  </FontAwesomeIcon>
                </Text>
              </div>
              {allStatewiseVmsLoading || statewiseVmsLoading ? (
                <Ellipsis />
              ) : (
                <div className={classes.icons}>
                  <div className={classes.iconWithCount}>
                    <div className={classes.iconContainer}>
                      <FontAwesomeIcon className={clsx(classes.statusIcon, classes.success)}>
                        circle-check
                      </FontAwesomeIcon>
                    </div>
                    <Text variant="body2">{allVmsState?.success}</Text>
                  </div>
                  <div className={classes.divider} />
                  <div className={classes.iconWithCount}>
                    <div className={classes.iconContainer}>
                      <FontAwesomeIcon className={clsx(classes.statusIcon, classes.warning)}>
                        circle-exclamation
                      </FontAwesomeIcon>
                    </div>
                    <Text variant="body2">{allVmsState?.warning}</Text>
                  </div>
                  <div className={classes.divider} />
                  <div className={classes.iconWithCount}>
                    <div className={classes.iconContainer}>
                      <FontAwesomeIcon className={clsx(classes.statusIcon, classes.error)}>
                        triangle-exclamation
                      </FontAwesomeIcon>
                    </div>
                    <Text variant="body2">{allVmsState?.error}</Text>
                  </div>
                </div>
              )}
            </div>
          </Card>
          {showAdminDashboard && (
            <Card className={classes.statusCard}>
              <div className={classes.cardBody}>
                <div className={classes.statusHeader}>
                  <Text variant="subtitle2">Hypervisors</Text>
                  <Text
                    variant="caption1"
                    className={classes.seeAll}
                    onClick={() => history.push(routeManager.routes.openstack.hosts.path())}
                  >
                    See All{' '}
                    <FontAwesomeIcon regular className={classes.arrow}>
                      chevron-right
                    </FontAwesomeIcon>
                  </Text>
                </div>
                <div className={classes.icons}>
                  <div className={classes.iconWithCount}>
                    <div className={classes.iconContainer}>
                      <FontAwesomeIcon className={clsx(classes.statusIcon, classes.success)}>
                        circle-check
                      </FontAwesomeIcon>
                    </div>
                    <Text variant="body2">{hypervisorCounts?.success}</Text>
                  </div>
                  <div className={classes.divider} />
                  <div className={classes.iconWithCount}>
                    <div className={classes.iconContainer}>
                      <FontAwesomeIcon className={clsx(classes.statusIcon, classes.error)}>
                        triangle-exclamation
                      </FontAwesomeIcon>
                    </div>
                    <Text variant="body2">{hypervisorCounts?.error}</Text>
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>
      )}
      {/* Show Quotas only when allTenants is false */}
      {!allTenants ? (
        <div className={classes.cards}>
          <Card className={classes.card}>
            <div className={classes.cardBody}>
              <div className={classes.cardHeader}>
                <div className={classes.iconContainer}>
                  <FontAwesomeIcon className={classes.icon} solid>
                    calculator
                  </FontAwesomeIcon>
                </div>
                <Text variant="subtitle2">Compute Quotas</Text>
              </div>
              <ProgressGraph
                label="Cores"
                usage={tenantUsage?.vcpus || 0}
                capacity={quotas?.cores?.limit}
              />
              <ProgressGraph
                label="RAM"
                usage={tenantUsage?.ram || 0}
                capacity={quotas?.ram?.limit}
                unit="MiB"
              />
              <ProgressGraph
                label="VMs"
                usage={quotas?.instances?.in_use}
                capacity={quotas?.instances?.limit}
              />
            </div>
          </Card>
          <Card className={classes.card}>
            <div className={classes.cardBody}>
              <div className={classes.cardHeader}>
                <div className={classes.iconContainer}>
                  <FontAwesomeIcon className={classes.icon} solid>
                    database
                  </FontAwesomeIcon>
                </div>
                <Text variant="subtitle2">Block Storage Quotas</Text>
              </div>
              <ProgressGraph
                label="Total Block Storage"
                usage={quotas?.gigabytes?.in_use}
                capacity={quotas?.gigabytes?.limit}
                unit="GB"
              />
              <ProgressGraph
                label="Volumes"
                usage={quotas?.volumes?.in_use}
                capacity={quotas?.volumes?.limit}
              />
              <ProgressGraph
                label="Volume Snapshots"
                usage={quotas?.snapshots?.in_use}
                capacity={quotas?.snapshots?.limit}
              />
            </div>
          </Card>
          <Card className={classes.card}>
            <div className={classes.cardBody}>
              <div className={classes.cardHeader}>
                <div className={classes.iconContainer}>
                  <FontAwesomeIcon className={classes.icon} solid>
                    server
                  </FontAwesomeIcon>
                </div>
                <Text variant="subtitle2">Network Quotas</Text>
              </div>
              <div className={classes.cardBody}>
                <ProgressGraph
                  label="Networks"
                  usage={quotas?.network?.used}
                  capacity={quotas?.network?.limit}
                />
                <ProgressGraph
                  label="Subnets"
                  usage={quotas?.subnet?.used}
                  capacity={quotas?.subnet?.limit}
                />
                <ProgressGraph
                  label="Routers"
                  usage={quotas?.router?.used}
                  capacity={quotas?.router?.limit}
                />
                <ProgressGraph
                  label="Ports"
                  usage={quotas?.port?.used}
                  capacity={quotas?.port?.limit}
                />
                <ProgressGraph
                  label="Public IPs"
                  usage={quotas?.floatingip?.used}
                  capacity={quotas?.floatingip?.limit}
                />
                <ProgressGraph
                  label="Security Groups"
                  usage={quotas?.security_group?.used}
                  capacity={quotas?.security_group?.limit}
                />
                <ProgressGraph
                  label="Security Group Rules"
                  usage={quotas?.security_group_rule?.used}
                  capacity={quotas?.security_group_rule?.limit}
                />
              </div>
            </div>
          </Card>
        </div>
      ) : (
        <>
          <Text variant="subtitle2">Compute</Text>
          <Divider className={classes.horizontalDivider} />
          <HostsListPageHeader hosts={hosts} />
          <UsageMetrics />
        </>
      )}
    </>
  )
}

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  quotasHeader: {
    borderBottom: `1px solid ${theme.components.table.border}`,
    paddingBottom: 8,
    marginBottom: 12,
  },
  headerText: {
    textTransform: 'uppercase',
    color: theme.components.typography.passive,
  },
  cards: {
    display: 'flex',
    gap: 16,
    alignItems: 'flex-start',
  },
  card: {
    width: '100%',
    flexGrow: 1,
  },
  cardBody: {
    display: 'grid',
    gap: 12,
  },
  cardHeader: {
    display: 'grid',
    alignItems: 'center',
    justifyContent: 'center',
    gridTemplateColumns: 'auto auto',
    gap: 8,
    marginBottom: 8,
  },
  iconContainer: {
    background: theme.palette.grey[100],
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 32,
    width: 32,
  },
  icon: {
    fontSize: '18px',
    color: theme.palette.grey[500],
  },
  center: {
    display: 'flex',
    justifyContent: 'center',
  },
  statistic: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    textAlign: 'center',
  },
  resourceStatuses: {
    marginBottom: 24,
    display: 'flex',
    gap: 8,
  },
  statusHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  statusCard: {
    flexGrow: 1,
    width: '100%',
  },
  icons: {
    display: 'flex',
    gridTemplateColumns: 'max-content max-content max-content max-content max-content',
    gap: 16,
  },
  iconWithCount: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  success: {
    background: theme.palette.graphGreen[100],
    color: theme.palette.graphGreen[500],
  },
  warning: {
    background: theme.palette.graphYellow[100],
    color: theme.palette.graphYellow[500],
  },
  error: {
    background: theme.palette.graphRed[100],
    color: theme.palette.graphRed[500],
  },
  seeAll: {
    cursor: 'pointer',
  },
  arrow: {
    fontSize: 12,
  },
  statusIcon: {
    fontSize: '18px',
    // color: theme.palette.grey[500],
  },
  dividerTop: {
    width: 1,
    height: 16,
    background: theme.components.card.text,
  },
  divider: {
    width: 1,
    height: 32,
    background: theme.components.card.text,
  },
  externalLink: {
    width: 'fit-content',
  },
  horizontalDivider: {
    margin: theme.spacing(1, 0, 3),
  },
  headerRefreshPortal: {
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: 'auto auto',
    alignItems: 'center',
  },
}))

export default DashboardPage
